import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2, Lock } from 'lucide-react';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const stripeAppearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#eab308',
    colorBackground: '#09090b',
    colorText: '#ffffff',
    colorTextSecondary: '#a1a1aa',
    colorDanger: '#f87171',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSizeBase: '15px',
    borderRadius: '12px',
    spacingUnit: '5px',
  },
  rules: {
    '.Input': {
      border: '1px solid #3f3f46',
      backgroundColor: '#18181b',
      color: '#ffffff',
      padding: '12px 14px',
    },
    '.Input:focus': {
      border: '1px solid #eab308',
      boxShadow: '0 0 0 3px rgba(234,179,8,0.15)',
    },
    '.Input::placeholder': { color: '#52525b' },
    '.Label': { color: '#a1a1aa', fontSize: '13px', marginBottom: '6px' },
    '.Tab': { border: '1px solid #3f3f46', backgroundColor: '#18181b', color: '#a1a1aa' },
    '.Tab--selected': { border: '1px solid #eab308', backgroundColor: '#18181b', color: '#ffffff' },
    '.Tab:hover': { border: '1px solid #52525b', color: '#ffffff' },
    '.TabIcon': { fill: '#a1a1aa' },
    '.TabIcon--selected': { fill: '#eab308' },
    '.Block': { backgroundColor: '#18181b', border: '1px solid #3f3f46' },
    '.Error': { color: '#f87171', fontSize: '13px' },
    // Hide Stripe Link "Save my information" block
    '.p-LinkAutofillSystemContainer': { display: 'none' },
    '.LinkAuthenticationElement': { display: 'none' },
  },
};

function CheckoutForm({ amount, onSuccess, onCancel, paymentMethodChoice }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setLoading(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.origin + '/booking-confirm' },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
      setLoading(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setError('Payment could not be completed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement
        options={{
          layout: { type: 'accordion', defaultCollapsed: false, radios: 'auto' },
          wallets: {
            applePay: 'auto',
            googlePay: 'never',
            link: 'never',
          },
          fields: { billingDetails: { address: { country: 'auto' } } },
          // Suppress Stripe Link "Save my information" block and Link-powered methods (Bank, Klarna)
          link: { display: 'never' },
        }}
      />

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <button
        type="submit"
        data-testid="stripe-pay-submit"
        disabled={!stripe || loading}
        className="w-full h-14 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold uppercase tracking-wider text-base flex items-center justify-center gap-2 transition-colors"
      >
        {loading ? (
          <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
        ) : (
          <><Lock className="w-4 h-4" /> Pay ${amount}</>
        )}
      </button>

      <button
        type="button"
        onClick={onCancel}
        className="w-full text-zinc-500 text-sm hover:text-zinc-400 transition-colors py-2"
      >
        Cancel
      </button>

      <p className="text-center text-zinc-600 text-xs flex items-center justify-center gap-1">
        <Lock className="w-3 h-3" /> Secured by Stripe
      </p>
    </form>
  );
}

export function StripePaymentSheet({ clientSecret, amount, onSuccess, onCancel, paymentMethodChoice }) {
  if (!clientSecret) return null;

  const options = { clientSecret, appearance: stripeAppearance };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm amount={amount} onSuccess={onSuccess} onCancel={onCancel} paymentMethodChoice={paymentMethodChoice} />
    </Elements>
  );
}
